import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Plus, Save, Trash2, X } from 'lucide-react';
import apiService from '../../services/api';
import {
  MAX_CUSTOM_QUICK_RESPONSE_LENGTH,
  MAX_CUSTOM_QUICK_RESPONSES,
  QUICK_RESPONSE_OPTIONS,
  normalizeQuickResponseLabel,
  sanitizeCustomQuickResponses,
} from '../../utils/quickResponses';
import { detectTextDirection } from '../../utils/textUtils';
import { directionForLanguage } from '../../i18n/config';
import './AnalyzeAgainModal.css';
import './ClientRequestPreferencesModal.css';

interface QuickResponsePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialCustomLabels: string[];
  onSaved?: () => void | Promise<void>;
}

export const QuickResponsePreferencesModal: React.FC<QuickResponsePreferencesModalProps> = ({
  isOpen,
  onClose,
  initialCustomLabels,
  onSaved,
}) => {
  const { t, i18n } = useTranslation('taskpane');
  const modalDir = directionForLanguage(i18n.language);
  const [customLabels, setCustomLabels] = React.useState<string[]>(initialCustomLabels);
  const [draft, setDraft] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      setCustomLabels(sanitizeCustomQuickResponses(initialCustomLabels));
      setDraft('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const addDraft = () => {
    const label = normalizeQuickResponseLabel(draft);
    if (!label) {
      setError(t('profile.quickResponseModal.enterPhrase'));
      return;
    }
    if (label.length > MAX_CUSTOM_QUICK_RESPONSE_LENGTH) {
      setError(t('profile.quickResponseModal.tooLong', { max: MAX_CUSTOM_QUICK_RESPONSE_LENGTH }));
      return;
    }
    const next = sanitizeCustomQuickResponses([...customLabels, label]);
    if (next.length === customLabels.length) {
      setError(t('profile.quickResponseModal.duplicate'));
      return;
    }
    if (next.length > MAX_CUSTOM_QUICK_RESPONSES) {
      setError(t('profile.quickResponseModal.maxCustom', { max: MAX_CUSTOM_QUICK_RESPONSES }));
      return;
    }
    setCustomLabels(next);
    setDraft('');
    setError(null);
  };

  const collectLabelsToSave = (): string[] | null => {
    const draftLabel = normalizeQuickResponseLabel(draft);
    if (!draftLabel) {
      return customLabels;
    }
    if (draftLabel.length > MAX_CUSTOM_QUICK_RESPONSE_LENGTH) {
      setError(t('profile.quickResponseModal.tooLong', { max: MAX_CUSTOM_QUICK_RESPONSE_LENGTH }));
      return null;
    }
    const next = sanitizeCustomQuickResponses([...customLabels, draftLabel]);
    if (next.length === customLabels.length) {
      setError(t('profile.quickResponseModal.duplicate'));
      return null;
    }
    if (next.length > MAX_CUSTOM_QUICK_RESPONSES) {
      setError(t('profile.quickResponseModal.maxCustom', { max: MAX_CUSTOM_QUICK_RESPONSES }));
      return null;
    }
    setCustomLabels(next);
    setDraft('');
    return next;
  };

  const handleSave = async () => {
    const labelsToSave = collectLabelsToSave();
    if (labelsToSave === null) return;

    setIsSaving(true);
    setError(null);
    try {
      await apiService.updateCustomQuickResponses(labelsToSave);
      if (onSaved) await onSaved();
      onClose();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : e?.message || t('profile.quickResponseModal.saveFailed'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="aa-modal-backdrop cr-prefs-modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSaving) onClose();
      }}
    >
      <div
        className="aa-modal cr-prefs-modal"
        dir={modalDir}
        role="dialog"
        aria-modal="true"
        aria-labelledby="qr-prefs-modal-title"
      >
        <div className="aa-modal-header">
          <div className="aa-modal-header-text">
            <h2 id="qr-prefs-modal-title" className="aa-modal-title">
              {t('profile.quickResponse')}
            </h2>
            <p className="aa-modal-subtitle">{t('profile.quickResponseModal.subtitle')}</p>
          </div>
          <button
            type="button"
            className="aa-modal-close"
            onClick={onClose}
            disabled={isSaving}
            aria-label={t('actions.close', { ns: 'common' })}
          >
            <X size={18} />
          </button>
        </div>

        {error && <div className="cr-prefs-error">{error}</div>}

        <div className="cr-prefs-groups">
          <div className="cr-prefs-group">
            <div className="cr-prefs-group-header">
              <MessageSquare size={16} className="cr-prefs-group-icon" />
              <span className="cr-prefs-group-title">{t('profile.quickResponseModal.builtIn')}</span>
            </div>
            <ul className="cr-prefs-options">
              {QUICK_RESPONSE_OPTIONS.map((option) => (
                <li
                  key={option.id}
                  className="cr-prefs-option cr-prefs-option-disabled"
                  dir={detectTextDirection(option.label)}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          </div>

          <div className="cr-prefs-group">
            <div className="cr-prefs-group-header">
              <span className="cr-prefs-group-title">{t('profile.quickResponseModal.custom')}</span>
              <span className="cr-prefs-group-badge">
                {customLabels.length}/{MAX_CUSTOM_QUICK_RESPONSES}
              </span>
            </div>
            {customLabels.length === 0 ? (
              <p className="aa-modal-lead">{t('profile.quickResponseModal.empty')}</p>
            ) : (
              <ul className="cr-prefs-options">
                {customLabels.map((label) => (
                  <li key={label} className="cr-prefs-option cr-prefs-custom-row">
                    <span dir={detectTextDirection(label)}>[{label}]</span>
                    <button
                      type="button"
                      className="cr-prefs-remove"
                      onClick={() => setCustomLabels((prev) => prev.filter((item) => item !== label))}
                      aria-label={t('profile.quickResponseModal.removeAria', { label })}
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="cr-prefs-add-row">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addDraft();
                  }
                }}
                maxLength={MAX_CUSTOM_QUICK_RESPONSE_LENGTH}
                placeholder="דורש אישור ועדה"
                dir="auto"
                className="cr-prefs-input"
              />
              <button
                type="button"
                className="aa-btn-secondary"
                onClick={addDraft}
                disabled={customLabels.length >= MAX_CUSTOM_QUICK_RESPONSES}
              >
                <Plus size={14} aria-hidden />
                {t('profile.quickResponseModal.add')}
              </button>
            </div>
          </div>
        </div>

        <div className="aa-modal-actions cr-prefs-actions">
          <button type="button" className="aa-btn-secondary" onClick={onClose} disabled={isSaving}>
            {t('actions.cancel', { ns: 'common' })}
          </button>
          <button type="button" className="aa-btn-primary" onClick={handleSave} disabled={isSaving}>
            <Save size={14} aria-hidden />
            {isSaving ? t('status.saving', { ns: 'common' }) : t('actions.save', { ns: 'common' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickResponsePreferencesModal;
